import { inject, NgModule } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterModule, RouterStateSnapshot, Routes } from '@angular/router';
import { JupiterHomeComponent } from './home/home.component';
import { JupiterSplashComponent } from './splash/splash.component';
import { JupiterDetailsComponent } from './details/details.component';
import { JupiterTabComponent } from './details/tab/tab.component';
import { JupiterActivityTabComponent } from './@components/tabs/activity/activity.component';
import { of } from 'rxjs';
import { JupiterEngagementTabComponent } from './@components/tabs/engagements/engagements.component';
import { JupiterCrmTaskTabComponent } from './@components/tabs/crm-task/crm-task.component';
import { ContactTaskDetailsComponent } from '../contacts/task/contact-task-details.component';
import { ContactTaskResolver } from '../contacts/contacts.resolvers';
import { CanDeactivateContactsDetails } from '../contacts/contacts.guards';
import { ContactsService } from '../contacts/contacts.service';
import { Store } from '@ngxs/store';
import { JupiterState } from './@store/state';
import { Contact } from '../contacts/contacts.types';
import { JupiterLayoutManagerComponent } from './@components/layout-manager/layout-manager.component';
import { DispositionsService } from '../projects/@shared/services/dispositions.service';
import { RelatedToActions } from '@jupiter-actions';
import { ProjectDataResolver } from '../projects/projects.resolvers';
import { resolveTitle } from './functions';
import { Title } from '@angular/platform-browser';

const routes: Routes = [
  {
    path: '',
    component: JupiterSplashComponent,
    children: [],
    title: () => 'Redirecting...',
  },
  {
    path: ':viewId',
    component: JupiterHomeComponent,
    children: [],
    title: () => resolveTitle('Home'),
    resolve: {
      options: ProjectDataResolver,
    },
    canDeactivate: [
      (
        component: any,
        activatedRoute: ActivatedRouteSnapshot,
        routerState: RouterStateSnapshot,
        nextRouterState: RouterStateSnapshot,
      ) => {
        if (!nextRouterState.url.startsWith('/space')) {
          inject(Store).dispatch(new RelatedToActions.Clear());
          inject(Title).setTitle('TermSheet');
        }

        return true;
      },
    ],
  },
  {
    path: ':viewId/:recordId',
    component: JupiterDetailsComponent,
    resolve: {
      options: ProjectDataResolver,
    },
    title: () => resolveTitle('Details'),
    canDeactivate: [
      (
        component: any,
        activatedRoute: ActivatedRouteSnapshot,
        routerState: RouterStateSnapshot,
        nextRouterState: RouterStateSnapshot,
      ) => {
        if (!nextRouterState.url.startsWith('/space')) {
          inject(Store).dispatch(new RelatedToActions.Clear());
          inject(Title).setTitle('TermSheet');
        }

        return true;
      },
    ],
    children: [
      {
        path: 'tab/:tabId',
        component: JupiterTabComponent,
      },
      {
        path: 'notes',
        title: () => resolveTitle('Notes'),
        canActivate: [
          (
            component: any,
            activatedRoute: ActivatedRouteSnapshot,
            routerState: RouterStateSnapshot,
            nextRouterState: RouterStateSnapshot,
          ) => {
            const store = inject(Store);
            const router = inject(Router);

            if (!store.selectSnapshot(JupiterState.getRelatedTo)) {
              router.navigateByUrl(`/space/void?return=${activatedRoute.url}`, { skipLocationChange: true });
              return;
            }

            return true;
          },
        ],
        loadChildren: () => import('@shared/modules/notes/notes.module').then((m) => m.NotesModule),
      },
      {
        path: 'folders',
        title: () => resolveTitle('Files'),
        loadChildren: () => import('@shared/modules/folders/folders.module').then((m) => m.FoldersTabModule),
      },
      {
        path: 'activities',
        component: JupiterActivityTabComponent,
        title: () => resolveTitle('Activities'),
      },
      {
        path: 'engagements',
        component: JupiterEngagementTabComponent,
        title: () => resolveTitle('Engagements'),
      },
      {
        path: 'tasks',
        title: () => resolveTitle('Tasks'),
        canActivate: [
          (
            component: any,
            activatedRoute: ActivatedRouteSnapshot,
            routerState: RouterStateSnapshot,
            nextRouterState: RouterStateSnapshot,
          ) => {
            const store = inject(Store);
            const router = inject(Router);

            if (!store.selectSnapshot(JupiterState.getRelatedTo)) {
              router.navigateByUrl(`/space/void?return=${activatedRoute.url}`, { skipLocationChange: true });
              return;
            }

            return true;
          },
        ],
        loadChildren: () => import('@tasks/tasks.routes').then((m) => m.routes),
      },
      {
        // hopefully this will go away
        path: 'crm-tasks',
        component: JupiterCrmTaskTabComponent,
        title: () => resolveTitle('Tasks'),
        children: [
          {
            path: ':task_list_id/:task_id',
            component: ContactTaskDetailsComponent,
            resolve: {
              task: ContactTaskResolver,
              contact: () => {
                const contactService = inject(ContactsService);
                const store = inject(Store);
                const contact = store.selectSnapshot(JupiterState.getEntity);
                contactService._contact.next(contact as unknown as Contact);
                return of(null);
              },
            },
            canDeactivate: [CanDeactivateContactsDetails],
            outlet: 'task',
          },
        ],
      },
      {
        path: 'leases',
        title: () => resolveTitle('Leases'),
        canActivate: [
          (activatedRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
            return true;
          },
        ],
        loadChildren: () => import('../properties/details/tenants/tenants.module').then((m) => m.PropertyTenantsModule),
      },
      {
        path: 'deals',
        title: () => resolveTitle('Deals'),
        loadChildren: () => import('../properties/details/deals/deals.module').then((m) => m.PropertyDealsModule),
      },
      {
        path: 'nearby',
        title: () => resolveTitle('Nearby'),
        loadChildren: () => import('../properties/details/nearby/nearby.module').then((m) => m.PropertyNearbyModule),
      },
      {
        path: 'dispositions',
        title: () => resolveTitle('Dispositions'),
        canActivate: [
          (activatedRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
            const dispositionsService = inject(DispositionsService);
            const store = inject(Store);
            dispositionsService.projectId = String(store.selectSnapshot(JupiterState.getEntity).id);
            return true;
          },
        ],
        loadChildren: () =>
          import('../projects/details/dispositions/dispositions.module').then((m) => m.DispositionsModule),
      },
      {
        path: 'loans',
        title: () => resolveTitle('Loans'),
        loadChildren: () => import('../projects/details/loans/loans.module').then((m) => m.ProjectLoansModule),
      },
      {
        path: 'contacts',
        title: () => resolveTitle('Contacts'),
        loadChildren: () => import('../projects/details/contacts/contacts.module').then((m) => m.ContactsModule),
      },
      {
        path: 'moon/:relatedToKey/:viewId',
        title: () => resolveTitle('Home'),
        resolve: {
          context: (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
            return of({ relatedTo: route.params.relatedTo });
          },
        },
        loadComponent: () => import('./home/home.component').then((m) => m.JupiterHomeComponent),
      },
      {
        path: 'layout-manager',
        title: () => resolveTitle('Layout Manager'),
        component: JupiterLayoutManagerComponent,
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class JupiterRoutingModule {}
